import type { CardProps } from "@/theme"
import { Card, IconButton, Theme, useBreakpointValue } from "@/theme"
import { useStyle } from "react-native-style-utilities"
import React from "react"
import type { DimensionValue } from "react-native"

/**
 * Set to true by an ancestor (`FloatingMapCardStack`) when the card is
 * rendered inside a parent that already handles positioning + vertical
 * stacking. In that case FloatingMapCard renders as a normal block
 * element so the parent's flex layout actually gets to stack siblings.
 *
 * Defaults to false (legacy behavior — self-positioned absolute card)
 * so screens that consume FloatingMapCard outside the stack
 * (`create-field.web.tsx`, `set-location-permanent.web.tsx`, etc.)
 * keep working without changes.
 */
const FloatingMapCardDockedContext = React.createContext<boolean>(false)

/**
 * Wrap a stack of cards (legend + selected-item panel + builder panel
 * + ...) so they share a single column container on the right side of
 * the map, and so any nested FloatingMapCard knows to skip its own
 * absolute positioning. The parent (StatusMap.web.tsx) handles
 * absolute positioning + gap between siblings.
 */
export function FloatingMapCardStack({
  children,
}: {
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <FloatingMapCardDockedContext.Provider value={true}>
      {children}
    </FloatingMapCardDockedContext.Provider>
  )
}

/**
 * Read the docked flag inside a FloatingMapCardStack. Components that
 * don't use FloatingMapCard but live in the same right-side stack
 * (e.g. PivotControlSidebar with its own absolute positioning) use this
 * to opt into the parent's flex layout.
 */
export function useFloatingMapCardDocked(): boolean {
  return React.useContext(FloatingMapCardDockedContext)
}

export function FloatingMapCard({
  id = "floatingMapCard",
  onClose,
  style,
  ...rest
}: CardProps & {
  onClose?: () => void
}): React.JSX.Element {
  const isDocked = React.useContext(FloatingMapCardDockedContext)
  const getValue = useBreakpointValue()
  const right = getValue({ base: 0, md: Theme.SPACING.$2 })
  const top = getValue<DimensionValue>({ base: "auto", md: Theme.SPACING.$2 })
  const bottom = getValue<DimensionValue>({ base: 0, md: "auto" })
  return (
    <Card
      id={id}
      actionElement={
        onClose ? (
          <IconButton
            IconComponent={"Close"}
            size={"sm"}
            variant={"text"}
            onPress={onClose}
          />
        ) : undefined
      }
      style={useStyle(() => {
        const baseStyle = isDocked
          ? {
              // Parent column controls layout. We just declare width
              // limits so a card doesn't outgrow the right-edge stack.
              maxWidth: Theme.SIZES.$lg,
              minWidth: Theme.SIZES.$xs,
              overflow: "hidden" as const,
              width: "100%" as const,
            }
          : {
              bottom,
              maxWidth: Theme.SIZES.$lg,
              minWidth: Theme.SIZES.$xs,
              overflow: "hidden" as const,
              position: "absolute" as const,
              right,
              top,
              width: "100%" as const,
              zIndex: Theme.Z_INDEX.fab,
            }
        return [baseStyle, style]
      }, [bottom, isDocked, right, style, top])}
      {...rest}
    />
  )
}
